import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Printer, ArrowLeft, CheckCircle2, Calendar, Mail, Phone, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useHotel } from '../context/HotelContext';
import apiClient from '../lib/apiClient';

const PrintTicket = ({ item, order, hotel }) => {
  const color = item.ticketTypeColor || '#2563EB';
  const isNormal = item.typeId !== 1;

  return (
    <div className="print-ticket flex rounded-2xl overflow-hidden shadow-xl mb-6 print:shadow-none print:mb-4" style={{ minHeight: 180 }}>
      {/* Left stub */}
      <div
        className="w-20 flex flex-col items-center justify-between py-5 px-2 relative flex-shrink-0"
        style={{ background: color }}
      >
        <div className="absolute -right-3.5 top-4 w-7 h-7 rounded-full bg-white z-10 print:bg-white" />
        <div className="absolute -right-3.5 bottom-4 w-7 h-7 rounded-full bg-white z-10 print:bg-white" />

        <p className="text-white/80 text-xs font-bold [writing-mode:vertical-rl] rotate-180 tracking-widest select-none">
          {order.orderRef}
        </p>

        <div className="text-center">
          <p className="text-white font-black text-sm">{item.ticketTypeCode}</p>
          <p className="text-white/70 text-xs mt-0.5">{item.quantity} {item.quantity !== 1 ? 'tickets' : 'ticket'}</p>
        </div>
      </div>

      {/* Divider */}
      <div className="w-0 border-l-2 border-dashed border-white/40 relative" style={{ background: color }}>
        <div className="absolute -left-3.5 top-4 w-7 h-7 rounded-full bg-white z-10 print:bg-white" />
        <div className="absolute -left-3.5 bottom-4 w-7 h-7 rounded-full bg-white z-10 print:bg-white" />
      </div>

      {/* Main body */}
      <div className="flex-1 p-5 flex flex-col justify-between" style={{ background: color }}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-white/70 text-xs font-medium uppercase tracking-wider mb-0.5">{hotel?.hotelName}</p>
            <h2 className="text-white font-black text-xl leading-tight">{item.ticketTypeName}</h2>
            <p className="text-white/80 text-sm mt-0.5">{isNormal ? 'General Admission' : 'Corporate Pass'}</p>
          </div>
          <div className="text-right">
            <p className="text-white/70 text-xs">per ticket</p>
            <p className="text-white font-black text-2xl">৳{Number(item.unitPrice).toLocaleString()}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-3">
          <div className="bg-white/20 rounded-lg px-3 py-1.5 text-white text-xs flex items-center gap-1.5">
            <Calendar size={12} />
            <span>{order.visitDate}</span>
          </div>
          <div className="bg-white/20 rounded-lg px-3 py-1.5 text-white text-xs flex items-center gap-1.5">
            <User size={12} />
            <span>{order.leadName}</span>
          </div>
          {item.inclusions?.length > 0 && item.inclusions.slice(0, 3).map((inc, i) => (
            <div key={i} className="bg-white/20 rounded-lg px-3 py-1.5 text-white text-xs flex items-center gap-1.5">
              <CheckCircle2 size={12} />
              <span>{inc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const TicketPrintPage = () => {
  const { orderRef } = useParams();
  const navigate = useNavigate();
  const { hotel } = useHotel();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    apiClient.get(`/api/Ticket/Order/${orderRef}`)
      .then(data => setOrder(data))
      .catch(() => setError('Could not load ticket. Please check your order reference.'))
      .finally(() => setLoading(false));
  }, [orderRef]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error || !order) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="text-center">
        <p className="text-red-600 mb-4">{error || 'Order not found.'}</p>
        <Button variant="outline" onClick={() => navigate('/tickets')}>Back to Tickets</Button>
      </div>
    </div>
  );

  const totalTickets = order.items.reduce((s, i) => s + i.quantity, 0);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Action bar — hidden on print */}
      <div className="print:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <button onClick={() => navigate('/tickets')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="flex items-center gap-3">
          <p className="text-green-600 font-semibold text-sm flex items-center gap-1.5">
            <CheckCircle2 size={16} /> Order Confirmed!
          </p>
          <Button onClick={() => window.print()} className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2">
            <Printer size={16} /> Print Tickets
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8 print:py-4 print:px-0">
        {/* Header */}
        <div className="text-center mb-8 print:mb-6">
          {hotel?.logoUrl && (
            <img src={hotel.logoUrl} alt={hotel.hotelName} className="h-10 mx-auto mb-3 object-contain" />
          )}
          <h1 className="text-2xl font-bold text-gray-900">{hotel?.hotelName}</h1>
          <p className="text-gray-500 text-sm mt-1">Resort Ticket Confirmation</p>
        </div>

        {/* Order summary strip */}
        <div className="bg-white rounded-2xl p-5 mb-6 shadow-sm border border-gray-100 print:shadow-none print:border print:border-gray-200">
          <div className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wide">Order Ref</p>
              <p className="font-mono font-bold text-gray-900 text-lg">{order.orderRef}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wide">Visit Date</p>
              <p className="font-semibold text-gray-900 flex items-center gap-1"><Calendar size={13} className="text-blue-500" />{order.visitDate}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wide">Total Tickets</p>
              <p className="font-semibold text-gray-900">{totalTickets} ticket{totalTickets !== 1 ? 's' : ''}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs uppercase tracking-wide">Total Paid</p>
              <p className="font-bold text-blue-600 text-lg">৳{Number(order.totalAmount).toLocaleString()}</p>
            </div>
          </div>

          <div className="border-t mt-4 pt-4 flex flex-wrap gap-4 text-sm text-gray-600">
            <span className="flex items-center gap-1.5"><User size={14} className="text-blue-500" />{order.leadName}</span>
            <span className="flex items-center gap-1.5"><Mail size={14} className="text-blue-500" />{order.leadEmail}</span>
            {order.leadPhone && <span className="flex items-center gap-1.5"><Phone size={14} className="text-blue-500" />{order.leadPhone}</span>}
          </div>
        </div>

        {/* Tickets */}
        <div>
          <p className="text-gray-500 text-sm font-medium mb-4 print:hidden">Your Tickets</p>
          {order.items.map(item => (
            <PrintTicket key={item.id} item={item} order={order} hotel={hotel} />
          ))}
        </div>

        {/* Footer note */}
        <div className="mt-8 text-center text-xs text-gray-400 print:mt-4">
          <p>Please present this ticket at the resort entrance.</p>
          <p className="mt-1">This ticket is non-transferable. One ticket per person.</p>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .print-ticket { break-inside: avoid; }
        }
      `}</style>
    </div>
  );
};

export default TicketPrintPage;
